import {Icon} from '@atoms';
import {useBreakPoint, useModal} from '@hooks';
import {trackEvent} from '@lib/infrastructure';
import {ConnectionUser, getAllConnectedUsers, getIsLoadingConnections, useAppSelector} from '@lib/store';
import {Avatar} from '@molecules';
import {FlexCol, FlexRow, H3, H4, Subtitle} from '@quarks';
import {cn} from '@utils';
import {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {useConnectionsSchedule} from '@lib/store/src/ui/hooks/timeslots/useConnectionsSchedule';
import {constructConnectionsOverview} from '@lib/store/src/data/timeslots/viewmodel-helpers/constructConnectionsOverview';
import {ConnectionLocation, ConnectionOnMapModal} from './ConnectionOnMapModal';
import {NoConnections, NoOneThere} from './EmptyState';
import {ConnectionsLoader} from './ConnectionsLoader';
import {ModalPage} from '../../ModalPage';
import {Workspace} from '@lib/store/src/data/timeslots/apiTypes';
import {timeLabel, isFullDay} from '@lib/store/src/data/timeslots/dateTimeHelpers';

export const ConnectionOverview = ({
  dateKey,
  asBigPage,
  hideTitle,
  buildingId,
}: {
  dateKey: string;
  asBigPage?: boolean;
  hideTitle?: boolean;
  buildingId?: string;
}) => {
  const bp = useBreakPoint();
  const {t} = useTranslation();

  const {setBigPages, addPages, openModal, closeModal} = useModal();

  const showConnectionOnMap = (user: ConnectionUser, location: ConnectionLocation) => {
    const mapView = (
      <ConnectionOnMapModal
        user={user}
        workspace={location}
      />
    );

    trackEvent('Home_ConnectionsOverview__ExpandMap');

    addPages([mapView]);
    if (asBigPage) setBigPages([mapView]);
    openModal();
  };

  const connectionsSchedule = useConnectionsSchedule(dateKey);
  const connectionsLoading = useAppSelector(getIsLoadingConnections);
  const connectedUsers = useAppSelector(getAllConnectedUsers);
  const overview = constructConnectionsOverview({
    connectedUsers,
    connectionWorkdays: connectionsSchedule.workdays,
    connectionWorkspaceReservations: connectionsSchedule.workspaceResevations,
  });

  const connectionsForBuilding = overview.filter((o) => {
    return !buildingId || o.occupants[0]?.reservations[0]?.workspace.buildingId === buildingId;
  });

  if (connectionsLoading || connectionsSchedule.status === 'pending') return <ConnectionsLoader />;

  return (
    <FlexCol
      gap={16}
      className="w-full">
      {!hideTitle && (
        <FlexRow>
          <H3 className="pl-0">{t('screen:AllConnections')}</H3>
          {overview.length > 1 && (
            <button
              className="text-collaborative-blue-500* font-semibold text-sm"
              onClick={() => {
                addPages([
                  <ModalPage
                    title="Connections at the office"
                    onClose={closeModal}>
                    <div className="py-4">
                      <ConnectionOverview
                        hideTitle
                        dateKey={dateKey}
                      />
                    </div>
                  </ModalPage>,
                ]);
                openModal();
              }}>
              {t('ViewAll')}
            </button>
          )}
        </FlexRow>
      )}

      {connectedUsers.length === 0 && <NoConnections />}
      {connectionsForBuilding.length === 0 && <NoOneThere />}
      {connectionsForBuilding.map((o, i) => (
        <Fragment key={o.locationName + i}>
          {!buildingId && <H4 className="pl-0">{o.locationName}</H4>}
          <div
            className={cn('grid full-w gap-6 grid-cols-1', {
              'grid-cols-2': bp === 'large',
              'mb-5': i !== connectionsForBuilding.length - 1,
            })}>
            {o.occupants.map(({user, reservations}) => {
              const workspace = reservations.at(0)?.workspace;

              const workspaceLabel = reservations.map((r) => {
                const loc = getWorkspaceLabel(r.workspace);
                const time = isFullDay(r.localStart, r.localEnd) ? null : timeLabel(r.localStart, r.localEnd, t);

                return (
                  <span key={r.id}>
                    <span className="block">{loc}</span>
                    <span className="block">{time && `(${time})`}</span>
                  </span>
                );
              });

              return (
                <div
                  className={cn('flex w-full gap-2', {
                    'items-start': workspaceLabel.length > 0,
                  })}
                  key={user.id}>
                  <Avatar
                    className={cn('flex-none', {
                      'mt-1': workspaceLabel.length > 0,
                    })}
                    shouldShowCheckIn
                    user={user}
                  />
                  <div className="flex flex-col justify-center overflow-hidden mr-auto">
                    <p className="truncate">{user.name}</p>
                    {workspaceLabel.length > 0 && (
                      <Subtitle className="truncate">
                        <span className="flex flex-col gap-2">{workspaceLabel}</span>
                      </Subtitle>
                    )}
                  </div>
                  {workspace ? (
                    <button
                      className="flex-none"
                      aria-label={t('ShowOnMap')}
                      onClick={() => {
                        if ('areaName' in workspace) {
                          showConnectionOnMap(user, workspace);
                        }
                      }}>
                      <Icon
                        icon="map"
                        size="20px"
                        color="#767676"
                      />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Fragment>
      ))}
    </FlexCol>
  );
};

export const getWorkspaceLabel = (workspace?: Workspace) => {
  if (!workspace) return '';

  if ('areaName' in workspace && workspace.areaName) {
    return `${workspace.floorName ?? ''}${workspace.floorName && workspace.areaName ? ', ' : ''}${workspace.areaName}`;
  }

  if ('floorName' in workspace && workspace.floorName) {
    return workspace.floorName;
  }

  return '';
};
